import { useState, useId, forwardRef } from 'react';
import type { InputHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';

type PasswordInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label?: string;
  hint?: string;
  containerClassName?: string;
};

const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ label, hint, containerClassName = '', className = '', ...inputProps }, ref) => {
    const { t } = useTranslation();
    const [shown, setShown] = useState(false);
    const autoId = useId();
    const inputId = inputProps.id ?? autoId;

    const baseInputClass =
      'block w-full px-3 py-2 pr-10 bg-background text-foreground border border-input rounded-md text-body shadow-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary';

    return (
      <div className={containerClassName}>
        {label && (
          <label htmlFor={inputId} className="block text-body font-medium text-foreground mb-1">{label}</label>
        )}
        <div className="relative">
          <input
            ref={ref}
            type={shown ? 'text' : 'password'}
            className={`${baseInputClass} ${className}`}
            {...inputProps}
            id={inputId}
          />
          <button
            type="button"
            onClick={() => setShown((v) => !v)}
            className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground focus:outline-none"
            aria-label={shown ? t('common.hide_password', 'Skrij geslo') : t('common.show_password', 'Pokaži geslo')}
            tabIndex={-1}
          >
            {shown ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        {hint && <p className="text-caption text-muted-foreground mt-1">{hint}</p>}
      </div>
    );
  }
);

PasswordInput.displayName = 'PasswordInput';

export default PasswordInput;
